import React, { useMemo, useState } from "react";
import { matchPath, useLocation, useNavigate } from "react-router-dom";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Divider from "@mui/material/Divider";
import Drawer from "@mui/material/Drawer";
import IconButton from "@mui/material/IconButton";
import List from "@mui/material/List";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import ApiOutlinedIcon from "@mui/icons-material/ApiOutlined";
import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";
import AccountBalanceWalletRoundedIcon from "@mui/icons-material/AccountBalanceWalletRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import LogoutOutlinedIcon from "@mui/icons-material/LogoutOutlined";
import MenuRoundedIcon from "@mui/icons-material/MenuRounded";
import PublicIcon from "@mui/icons-material/Public";
import RocketLaunchOutlinedIcon from "@mui/icons-material/RocketLaunchOutlined";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";
import { alpha, darken } from "@mui/material/styles";
import { COMPANY, PRIMARYCOLOR } from "../../Constants";
import { useCredits } from "../../context/CreditsContext";
import "./topNav.css";

const brand = PRIMARYCOLOR || "#005CC5";

const pathMatches = (pathname, pattern, end = false) =>
  Boolean(matchPath({ path: pattern, end }, pathname));

function TopNav() {
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const { balance, loading: creditsLoading, error: creditsError } = useCredits();
  const availableCredits = Number(balance?.available_credits ?? 0);
  const creditLabel = creditsError
    ? "Credits unavailable"
    : creditsLoading && !balance
    ? "Loading credits"
    : `${new Intl.NumberFormat().format(availableCredits)} Credits`;
  const showZeroCreditState = !creditsError && !creditsLoading && availableCredits === 0;

  const isCreateFlow =
    location.pathname === "/pentests/create" ||
    location.pathname.startsWith("/pentest/create/");

  const navItems = useMemo(
    () => [
      {
        label: "Organizations",
        icon: <PublicIcon />,
        path: "/organizations",
        active: (pathname) =>
          pathMatches(pathname, "/organizations", true) ||
          pathMatches(pathname, "/organization/*"),
      },
      {
        label: "Pentests",
        icon: <ShieldOutlinedIcon />,
        path: "/pentests",
        active: (pathname) =>
          !isCreateFlow &&
          (pathMatches(pathname, "/pentests", true) ||
            pathMatches(pathname, "/pentest/*")),
      },
      {
        label: "Launch Pentest",
        icon: <RocketLaunchOutlinedIcon />,
        path: "/pentests/create",
        active: () => isCreateFlow,
      },
      {
        label: "Copilot",
        icon: <AutoAwesomeRoundedIcon />,
        path: "/copilot",
        active: (pathname) => pathMatches(pathname, "/copilot", true) || pathMatches(pathname, "/copilot/*"),
      },
      {
        label: "Reports",
        icon: <DescriptionOutlinedIcon />,
        path: "/reports",
        active: (pathname) => pathMatches(pathname, "/reports", true) || pathMatches(pathname, "/reports/*"),
      },
      {
        label: "Settings",
        icon: <SettingsOutlinedIcon />,
        path: "/settings/account",
        active: (pathname) => pathMatches(pathname, "/settings/*"),
      },
      {
        label: "Documentation",
        icon: <ApiOutlinedIcon />,
        path: "/api-docs",
        active: (pathname) => pathMatches(pathname, "/api-docs", true),
      },
    ],
    [isCreateFlow]
  );

  const goTo = (path) => {
    navigate(path);
    setMobileOpen(false);
  };

  const handleLogout = () => {
    navigate("/");
    setMobileOpen(false);
  };

  const cssVars = {
    "--tn-primary": brand,
    "--tn-primary-dark": darken(brand, 0.18),
    "--tn-primary-faint": alpha(brand, 0.08),
    "--tn-primary-ring": alpha(brand, 0.28),
  };

  const creditTooltip = (
    <Box sx={{ p: 0.6, minWidth: 230 }}>
      <Typography sx={{ fontWeight: 900, mb: 0.8 }}>{creditsError || `${creditLabel} available`}</Typography>
      {!creditsError ? (
        <Stack spacing={0.45}>
          <Typography variant="caption">Subscription: {new Intl.NumberFormat().format(Number(balance?.subscription_credits || 0))}</Typography>
          <Typography variant="caption">Purchased: {new Intl.NumberFormat().format(Number(balance?.purchased_credits || 0))}</Typography>
          <Typography variant="caption">Reserved: {new Intl.NumberFormat().format(Number(balance?.reserved_credits || 0))}</Typography>
          {balance?.credits_period_end ? <Typography variant="caption">Period ends: {new Date(balance.credits_period_end).toLocaleDateString()}</Typography> : null}
        </Stack>
      ) : null}
    </Box>
  );

  const mobileMenu = (
    <Box className="tn-mobile-panel" style={cssVars}>
      <Box className="tn-mobile-header">
        <Box className="tn-brand" onClick={() => goTo("/pentests")} role="button" tabIndex={0}>
          <Box className="tn-logo">{COMPANY.charAt(0)}</Box>
          <Box>
            <Typography className="tn-brand-title">{COMPANY}</Typography>
            <Typography className="tn-brand-subtitle">Security operations platform</Typography>
          </Box>
        </Box>
        <IconButton aria-label="Close navigation" onClick={() => setMobileOpen(false)}>
          <CloseRoundedIcon />
        </IconButton>
      </Box>

      <List className="tn-mobile-list">
        {navItems.map((item) => {
          const active = item.active(location.pathname);
          return (
            <ListItemButton
              key={item.path}
              selected={active}
              onClick={() => goTo(item.path)}
              className="tn-mobile-item"
            >
              <ListItemIcon className="tn-mobile-icon">{item.icon}</ListItemIcon>
              <ListItemText primary={item.label} />
            </ListItemButton>
          );
        })}
      </List>

      <Button
        fullWidth
        startIcon={<AccountBalanceWalletRoundedIcon />}
        onClick={() => goTo("/billing")}
        className={`tn-mobile-credits ${showZeroCreditState ? "is-empty" : ""}`}
      >
        {creditLabel}
      </Button>

      <Divider className="tn-mobile-divider" />

      <Button
        fullWidth
        startIcon={<LogoutOutlinedIcon />}
        onClick={handleLogout}
        className="tn-mobile-logout"
      >
        Logout
      </Button>
    </Box>
  );

  return (
    <Box className="top-nav-shell" style={cssVars}>
      <Box className="top-nav">
        <Box className="tn-brand" onClick={() => goTo("/pentests")} role="button" tabIndex={0}>
          <Box className="tn-logo">{COMPANY.charAt(0)}</Box>
          <Box className="tn-brand-copy">
            <Typography className="tn-brand-title">{COMPANY}</Typography>
            <Typography className="tn-brand-subtitle">AI Pentesting Agents</Typography>
          </Box>
        </Box>

        <Box component="nav" className="tn-links" aria-label="Primary navigation">
          {navItems.map((item) => {
            const active = item.active(location.pathname);
            return (
              <Button
                key={item.path}
                startIcon={item.icon}
                onClick={() => goTo(item.path)}
                className={`tn-link ${active ? "is-active" : ""}`}
                aria-current={active ? "page" : undefined}
              >
                {item.label}
              </Button>
            );
          })}
        </Box>

        <Box className="tn-actions">
          <Tooltip title={creditTooltip} arrow>
            <Button
              startIcon={<AccountBalanceWalletRoundedIcon />}
              onClick={() => goTo("/billing")}
              className={`tn-credit-balance ${showZeroCreditState ? "is-empty" : ""}`}
              aria-label={`${creditLabel}. Open credits and billing.`}
            >
              {creditLabel}
            </Button>
          </Tooltip>
          <Tooltip title="Logout" arrow>
            <IconButton aria-label="Logout" className="tn-logout-icon" onClick={handleLogout}>
              <LogoutOutlinedIcon />
            </IconButton>
          </Tooltip>
          <IconButton
            aria-label="Open navigation"
            className="tn-menu-button"
            onClick={() => setMobileOpen(true)}
          >
            <MenuRoundedIcon />
          </IconButton>
        </Box>
      </Box>

      <Drawer
        anchor="right"
        open={mobileOpen}
        onClose={() => setMobileOpen(false)}
        PaperProps={{ className: "tn-mobile-drawer" }}
      >
        {mobileMenu}
      </Drawer>
    </Box>
  );
}

export default TopNav;
